import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/solid";
import { DataTable, DataTableRowClickEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { useAppSelector } from "../../../app/hooks";
import { getParamAsInt } from "../../../app/utils";
import { localeShortDateTime } from "../../../app/datetimeUtils";
import FormattedDateAndTime from "../../../components/FormattedDateAndTime";
import { Loading } from "../../../components/Loading";
import { LoadingTextSkeleton } from "../../../components/LoadingTextSkeleton";
import Select from "../../../components/input/Select";
import { DraftList, UserTestRow, useViewDraftsQuery } from "./draftAutosaveHistoryApiSlice";
import DraftAutosaveHistoryModal from "./DraftAutosaveHistoryModal";

export default function ViewDraftAutosaveHistory() {
	// Initialization
	const navigate = useNavigate();
	const getQueryParams = () => {
		const [queryParams] = useSearchParams();
		const writerId = getParamAsInt(queryParams, "writerId");
		const courseId = useAppSelector((state) => state.user.course?.id);

		if (!writerId || !courseId) {
			navigate(`/writing`);
		}

		return {
			writerId: writerId!,
			courseId: courseId!,
		};
	};
	const { writerId, courseId } = getQueryParams();

	// Table
	const viewDraftsResult = useViewDraftsQuery({
		writerId,
		courseId,
	});
	const draftList: DraftList | undefined = viewDraftsResult.data;

	const popoverBodyTemplate = () => {
		return (
			<ArrowTopRightOnSquareIcon className="size-5 text-gray-400 group-hover:text-gray-600" />
		);
	};

	const dateBodyTemplate = (row: UserTestRow) => {
		return <FormattedDateAndTime date={row.dateTime} />;
	};

	const handleRowClick = (event: DataTableRowClickEvent) => {
		setDialogOpen(true);
		setSelectedDraftId(event.data.id);
		setSelectedPromptTitle(event.data.promptTitle);
		setSelectedDraftDate(localeShortDateTime(event.data.dateTime));
	};

	// Dialog
	const [dialogOpen, setDialogOpen] = useState<boolean>(false);
	const [selectedDraftId, setSelectedDraftId] = useState<number>();
	const [selectedPromptTitle, setSelectedPromptTitle] = useState<string>();
	const [selectedDraftDate, setSelectedDraftDate] = useState<string>();

	const resetDialog = () => {
		setDialogOpen(false);
		setSelectedDraftId(undefined);
		setSelectedPromptTitle(undefined);
		setSelectedDraftDate(undefined);
	};

	// Filtering
	const [selectedPrompt, setSelectedPrompt] = useState<string>();
	const [selectedStatus, setSelectedStatus] = useState<string>();

	const promptTitleOptions = Array.from(
		new Set(draftList?.drafts?.map((draft) => draft.promptTitle).filter(Boolean))
	);

	const statusOptions = Array.from(
		new Set(draftList?.drafts?.map((draft) => draft.status).filter(Boolean))
	);

	// Filter drafts by selected prompt and status
	const filteredDrafts = draftList?.drafts
		? draftList.drafts.filter((draft) => {
				const matchesPrompt =
					!selectedPrompt ||
					selectedPrompt === "All" ||
					draft.promptTitle === selectedPrompt;
				const matchesStatus =
					!selectedStatus || selectedStatus === "All" || draft.status === selectedStatus;
				return matchesPrompt && matchesStatus;
			})
		: [];

	return (
		<section>
			<h1>Draft Autosave History</h1>
			<p className="mt-4">
				Welcome to the Draft Autosave History page! From this page, you can view all drafts
				a student has written within the course. You can click on any draft in the list to
				open a detailed view. In the popover that appears, you&apos;ll see a full autosave
				history for that draft. To explore further, click on an autosave entry in the
				popover to view the saved text content from that moment in time.
			</p>
			{viewDraftsResult.isLoading ? (
				<LoadingTextSkeleton className="w-60! mt-8" />
			) : (
				<p className="mt-8 mb-4 text-lg font-medium leading-6 text-gray-900">
					Student: {draftList?.formalFullName}
				</p>
			)}
			<Loading loading={viewDraftsResult.isLoading} className="mt-4!" />
			{draftList && (
				<>
					<div className="flex gap-4">
						<Select
							value={selectedPrompt}
							setValue={(v) => setSelectedPrompt(v as string)}
							label="Prompt"
							simpleListBoxId="promptFilter"
							options={[
								{ value: "All", label: "All" },
								...promptTitleOptions.map((title) => ({
									value: title,
									label: title,
								})),
							]}
							simpleListBoxClasses="w-64"
						/>
						<Select
							value={selectedStatus}
							setValue={(v) => setSelectedStatus(v as string)}
							label="Status"
							simpleListBoxId="statusFilter"
							options={[
								{ value: "All", label: "All" },
								...statusOptions.map((status) => ({
									value: status,
									label: status,
								})),
							]}
							simpleListBoxClasses="w-64"
						/>
					</div>
					<DataTable
						value={filteredDrafts}
						dataKey="id"
						stripedRows
						emptyMessage="No results found"
						className="tbl tbl-data tbl-datatable pt-4"
						selectionMode="single"
						onRowClick={handleRowClick}
						rowClassName={() => "group cursor-pointer"}>
						<Column
							body={popoverBodyTemplate}
							headerClassName="!px-2"
							className="!px-2 "
						/>
						<Column
							header="Date"
							body={dateBodyTemplate}
							headerClassName="!px-2"
							className="!px-2"
						/>
						<Column
							header="Prompt"
							field="promptTitle"
							headerClassName="!px-2"
							className="!px-2"
						/>
						<Column
							header="Status"
							field="status"
							headerClassName="!px-2"
							className="!px-2"
						/>
					</DataTable>
				</>
			)}
			<DraftAutosaveHistoryModal
				draftId={selectedDraftId}
				promptTitle={selectedPromptTitle}
				draftDateTime={selectedDraftDate}
				dialogOpen={dialogOpen}
				resetDialog={resetDialog}
			/>
		</section>
	);
}
